import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  Add,
  BorderColorRounded,
  RoomOutlined,
} from "@mui/icons-material";

import PaymentMethodForm from "./user-forms/PaymentMethodForm";
import HomeAddressForm from "./user-forms/HomeAddressForm";
import UiModal from "../common/ui/UiModal";
import { ERRORS, ONBOARDING_STEPS, VIEWS } from "../constants/user-info.const";
import {
  createBankAccount,
  getBankAccountDetails,
  getEmployee,
  getPaymentMethod,
  updateEmpAddress,
  updatePaymentMethod,
} from "../../services/employeeService";
import Loader from "../common/Loader";
import { formSubmitErrorHandler } from "../../services/formService";
import { AppContext } from "../routing/Routing";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  setEmployeeDetails,
  updateEmployeeAddress,
} from "../../store/actions/employee";
import store from "../../store";
import { onboardingActions } from "../../store/actions/steps";
import {
  AddressInfoFormFields,
  BankFormFields,
  PAYMENT_TYPES,
} from "./models/user-interface";

function PaymentMethod(props: any) {
  const [isAccountModalOpen, setIsAccountModalOpen] = useState<boolean>(false);
  const [isMailingtModalOpen, setIsMailingtModalOpen] =
    useState<boolean>(false);
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  const [bankAccountDetails, setBankAccountDetails] = useState<any>({});
  const formRef: any = useRef();
  const { setViewName } = useContext(AppContext);
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [isShowFooterLoader, setIsShowFooterLoader] = useState(false);

  const {
    employee: { home_address },
    usStates,
    setEmployeeData,
    updateAddress,
    isNavigateToNext,
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalLoading, setModalLoading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      mountedRef && (mountedRef.current = false);
      nextButtonRef && (nextButtonRef.disabled = false);
    };
  }, []);
  useEffect(() => {
    setIsLoading(true);
    async function fetchData() {
      await getPaymentMethod()
        .then((response: any) => {
          if (!mountedRef.current) return null;
          setPaymentDetails(response?.data);
        })
        .catch(() => {
          if (!mountedRef.current) return null;
          setIsLoading(false);
        });
      await getBankAccountDetails().then((response: any) => {
        if (!mountedRef.current) return null;
        setBankAccountDetails(response?.data[0]);
      });
      await getEmployee().then((response: any) => {
        if (!mountedRef.current) return null;
        setEmployeeData(response?.data);
      });
      setIsLoading(false);
    }
    fetchData();
  }, []);

  const validationSchema = Yup.object({
    payment_method: Yup.string().required(ERRORS.paymentMethod),
  });

  const validationBankDetailsSchema = Yup.object({
    name: Yup.string().required(ERRORS.bankName),
    routing_number: Yup.string().required(ERRORS.routingNumber),
    account_number: Yup.string().required(ERRORS.accountNumber),
    account_type: Yup.string().required(ERRORS.accountType),
  });

  const memoizedState = useMemo(
    () => usStates?.find((v) => v.id === home_address?.state),
    [usStates, home_address]
  );

  const validationAddressSchema = Yup.object({
    street_1: Yup.string().required(ERRORS.street),
    street_2: Yup.string(),
    city: Yup.string().required(ERRORS.city),
    state: Yup.object().required(ERRORS.state).nullable(),
    zip: Yup.string().required(ERRORS.zipCode),
  });

  const onBankDetailsSubmit = (
    { name, routing_number, account_number, account_type }: BankFormFields,
    formHelpers: FormikHelpers<any>
  ) => {
    const data = {
      name,
      routing_number,
      account_number,
      account_type,
    };
    setModalLoading(true);
    formSubmitErrorHandler(
      createBankAccount(data).then((response: any) => {
        setBankAccountDetails(response?.data);
        const data = {
          ...paymentDetails,
          splits: [{ ...response.data }],
        };
        getPaymentMethod().then((response: any) => {
          setPaymentDetails(response?.data);
        });
        setPaymentDetails(data);
        setModalLoading(false);
        setIsAccountModalOpen(false);
      }),
      () => {
        setModalLoading(false);
      },
      formHelpers.setFieldError
    );
  };

  const onAdressSubmit = (
    { street_1, street_2, city, state, zip }: AddressInfoFormFields,
    formHelpers: FormikHelpers<any>
  ) => {
    const data = {
      version: home_address?.version,
      street_1,
      street_2,
      city,
      state: state?.id,
      zip: zip,
    };
    setModalLoading(true);
    formSubmitErrorHandler(
      updateEmpAddress(data).then((response: any) => {
        updateAddress(response.data);
        setModalLoading(false);
        setIsMailingtModalOpen(false);
      }),
      () => {
        setModalLoading(false);
      },
      formHelpers.setFieldError
    );
  };

  const navigationToReviewInfo = () => {
    if (isNavigateToNext === false) return;
    setViewName(VIEWS.reviewInfo);
    navigate("/" + VIEWS.reviewInfo);
  };

  const { splits } = paymentDetails;

  const updateCompletedStep = () => {
    store.dispatch({
      type: onboardingActions.UPDATE_ONBOARDING_STEPS,
      state: ONBOARDING_STEPS.paymentMethod,
    });
  };
  const handleSubmit = (values: any, formHelpers: FormikHelpers<any>) => {
    const data = {
      version: paymentDetails?.version,
      type: values.payment_method,
    };
    if (values?.payment_method === PAYMENT_TYPES.directDeposit) {
      data["splits"] = [
        {
          uuid: bankAccountDetails?.uuid,
          name: bankAccountDetails?.name,
        },
      ];
    }
    setIsShowFooterLoader(true);
    formSubmitErrorHandler(
      updatePaymentMethod(data).then(() => {
        setIsShowFooterLoader(false);
        navigationToReviewInfo();
        updateCompletedStep();
        formHelpers.resetForm();
      }),
      () => {
        setIsShowFooterLoader(false);
      },
      formHelpers.setFieldError
    );
  };
  const nextButtonRef: any = useMemo(
    () => document.getElementById("emp-next"),
    [paymentDetails]
  );
  const isNextButtonDisabled = (values) => {
    if (
      values.payment_method === PAYMENT_TYPES.directDeposit &&
      !(splits?.length > 0)
    ) {
      nextButtonRef && (nextButtonRef.disabled = true);
    } else {
      nextButtonRef && (nextButtonRef.disabled = false);
    }
  };
  return isLoading ? (
    <Loader />
  ) : (
      <>
    <div className="personal-info user-details__content">
      <Formik
        initialValues={{ payment_method: paymentDetails?.type }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={props.formRef}
      >
        {(formik, meta, form) => {
          isNextButtonDisabled(formik.values);
          return (
            <>
              <FormControl
                fullWidth
                error={
                  (meta?.touched || form?.submitCount > 0) &&
                  (meta?.error ? true : false)
                }
              >
                <RadioGroup
                  aria-labelledby="controlled-radio-buttons-group"
                  name="payment_method"
                  value={formik.values.payment_method}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel
                    value={PAYMENT_TYPES.directDeposit}
                    control={<Radio />}
                    label={PAYMENT_TYPES.directDeposit}
                  />
                  {formik?.values?.payment_method ===
                    PAYMENT_TYPES.directDeposit && (
                    <Box ml={5}>
                      <Box mb={2}>
                        <Typography variant="body2">
                          Your paycheck will be deposited to the provided bank
                          account:
                        </Typography>
                      </Box>
                      {!(splits?.length > 0) && (
                        <Box mb={2}>
                          <Box mb={2}>
                            <Typography variant="body2">
                              Bank Account (Required)
                            </Typography>
                          </Box>
                          <Button
                            startIcon={<Add />}
                            variant="outlined"
                            onClick={() => setIsAccountModalOpen(true)}
                          >
                            Add Bank Account
                          </Button>
                        </Box>
                      )}
                      {splits?.length > 0 && (
                        <Box display="flex" mb={2}>
                          <Box display="flex">
                            <Box mr={3}>
                              <Box>
                                <AccountBalanceOutlined />
                              </Box>
                            </Box>
                            <div className="payment-method--details-section">
                              <Typography variant="body2">
                                {bankAccountDetails?.name}
                              </Typography>
                              <Typography variant="body2">
                                Account: {bankAccountDetails?.account_number}
                              </Typography>
                              <Typography variant="body2">
                                Routing: {bankAccountDetails?.routing_number}
                              </Typography>
                              <Typography variant="body2">
                                {bankAccountDetails?.account_type}
                              </Typography>
                            </div>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  <FormControlLabel
                    value={PAYMENT_TYPES.check}
                    control={<Radio />}
                    label={PAYMENT_TYPES.check}
                  />
                  {formik?.values?.payment_method === PAYMENT_TYPES.check && (
                    <Box ml={5}>
                      <Box my={2}>
                        <Typography>
                          A check will be given to you by your employer.
                          1-800Accountant does not mail checks.
                        </Typography>
                      </Box>
                      <Box>
                        <Box mb={2}>
                          <Box mb={2}>
                            <Typography variant="body2">
                              Mailing Address (Required)
                            </Typography>
                          </Box>
                        </Box>
                        <Box mt={2} display="flex" alignItems="center">
                          <Box>
                            <RoomOutlined />
                          </Box>
                          {home_address?.street_1 && (
                            <div className="payment-method--details-section">
                              <Typography variant="body2">
                                <span>{home_address?.street_1}</span>{" "}
                                {home_address?.street_2 && (
                                  <span>
                                    <span>{home_address?.street_2}</span>
                                    {", "}
                                  </span>
                                )}
                                <span>{home_address?.city},</span>{" "}
                                <span>{memoizedState?.name || ""}</span>{" "}
                                <span>{home_address?.zip}</span>
                              </Typography>
                            </div>
                          )}
                          <Box>
                            <IconButton
                              onClick={() => setIsMailingtModalOpen(true)}
                            >
                              <BorderColorRounded />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </RadioGroup>
                <FormHelperText>
                  {meta?.touched || form?.submitCount > 0 ? meta?.error : ""}
                </FormHelperText>
              </FormControl>
             
            </>
          );
        }}
      </Formik>
      
      <UiModal
        open={isAccountModalOpen}
        handleClose={() => setIsAccountModalOpen(false)}
        title={"Add Bank Account"}
        handleSave={() => {
          formRef.current?.handleSubmit();
        }}
      >
        <Formik
          initialValues={{
            name: "",
            routing_number: "",
            account_number: "",
            account_type: "",
          }}
          onSubmit={onBankDetailsSubmit}
          validationSchema={validationBankDetailsSchema}
          enableReinitialize
          innerRef={formRef}
        >
          {(formik) => {
            return isModalLoading ? (
              <Loader />
            ) : (
              <Form>
                <div>
                  <Typography>
                    Your paycheck will be deposited to the provided bank
                    account:
                  </Typography>
                  <PaymentMethodForm />
                </div>
              </Form>
            );
          }}
        </Formik>
      </UiModal>
      <UiModal
        open={isMailingtModalOpen}
        handleClose={() => setIsMailingtModalOpen(false)}
        title={"Add Mailing Address"}
        handleSave={() => {
          formRef.current?.handleSubmit();
        }}
      >
        <Formik
          initialValues={{
            street_1: home_address?.street_1 || "",
            street_2: home_address?.street_2 || "",
            city: home_address?.city || "",
            state: memoizedState,
            zip: home_address?.zip || "",
          }}
          onSubmit={onAdressSubmit}
          validationSchema={validationAddressSchema}
          enableReinitialize
          innerRef={formRef}
        >
          {(formik) => {
            return isModalLoading ? (
              <Loader />
            ) : (
              <Form>
                <HomeAddressForm states={usStates} />
              </Form>
            );
          }}
        </Formik>
      </UiModal>
      
    </div>
    {isShowFooterLoader && <Loader footerLoader={true} />}
    </>
  );
}

const mapStateToProps = (state) => ({
  employee: state.employee.details,
  usStates: state.config?.employees?.states,
  isNavigateToNext: state.navigationFromMenu.isNavigateToNext,
});

export default connect(mapStateToProps, {
  setEmployeeData: setEmployeeDetails,
  updateAddress: updateEmployeeAddress,
})(PaymentMethod);
